import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
    Formik,
    Form,
    Field,
    FormikValues,
    FormikHandlers,
    FormikHelpers,
} from "formik";
import {
    Autocomplete,
    MenuItem,
    TextField,
    createFilterOptions,
} from "@mui/material";
import "../config.styles.css";
import * as yup from "yup";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import {
    COLLECTIONS,
    CW_CAROUSEL,
    EPISODIC_RAIL,
    GENRE,
    HERO_CAROUSEL,
    HOME_PAGE,
    HORIZONTAL_RAIL,
    MOVIES_PAGE,
    MY_LIST,
    NO_CONTENT_PLAYLIST,
    POPULAR_SEARCH,
    RAIL_TEXT_LIMIT,
    SERIES_PAGE,
    SINGULAR_PROMO,
    THEMATIC,
} from "../LandingPage/constants";
import { getPlaylistCount } from "../../../utilities/helpers/helperService";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

export default function CreatePagePopup({
    show,
    handleCloseCB,
    handleAddRail,
    railList,
    selectedRail,
    handleEditRail,
    playlist,
    template,
}: any) {
    // validation schema
    const validationSchemaInitWithPlaylist = yup.object({
        railTitle: yup
            .string()
            .required("Rail should be required")
            .max(
                RAIL_TEXT_LIMIT,
                `Rail title cannot exceed ${RAIL_TEXT_LIMIT} characters`
            ),
        template: yup.string().required("Template is mandatory"),
        playlistId: yup.string().required("Playlist ID is mandatory"),
        playlistCount: yup.number().required().min(1),
    });

    const validationSchemaInitWithoutPlaylist = yup.object({
        railTitle: yup
            .string()
            .required("Rail should be required")
            .max(
                RAIL_TEXT_LIMIT,
                `Rail title cannot exceed ${RAIL_TEXT_LIMIT} characters`
            ),
        template: yup.string().required("Template is mandatory"),
    });

    const validationSchemaTemplateOnly = yup.object({
        template: yup.string().required("Template is mandatory"),
    });

    const [open, setOpen] = useState(show);
    const [submitting, setSubmitting] = useState(false);
    const [masterData, setMasterData] = useState<any>([]);
    const { enqueueSnackbar } = useSnackbar();
    const [isEditRail, setIsEditRail] = useState(false);
    const [playlistInfo, setPlaylistInfo] = useState<any>([]);
    const [isOpen, setIsOpen] = React.useState(false);
    const [validationSchema, setValidationSchema] = useState<any>(
        validationSchemaInitWithPlaylist
    );

    const handleOpenOptions = () => {
        setIsOpen(true);
    };

    const handleCloseOptions = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        getMasterInfo();
    }, []);

    useEffect(() => {
        setPlaylistInfo(playlist);
    }, [playlist]);

    useEffect(() => {
        if (selectedRail?.payload) {
            setIsEditRail(true);
            if (
                selectedRail?.payload?.template == CW_CAROUSEL ||
                selectedRail?.payload?.template == GENRE ||
                selectedRail?.payload?.template == HERO_CAROUSEL ||
                selectedRail?.payload?.template == THEMATIC ||
                selectedRail?.payload?.template == SINGULAR_PROMO ||
                selectedRail?.payload?.template == EPISODIC_RAIL ||
                selectedRail?.payload?.template == MY_LIST
            ) {
                setValidationSchema(validationSchemaInitWithoutPlaylist);
            } else if (selectedRail?.payload?.template == COLLECTIONS) {
                setValidationSchema(validationSchemaTemplateOnly);
            }
        }
    }, [selectedRail]);

    // set initial values
    const initialValues: FormikValues = {
        railTitle: selectedRail?.payload?.railTitle || "",
        template: selectedRail?.payload?.template || "",
        playlistId: selectedRail?.payload?.playlistId || "",
        playlistCount: selectedRail?.payload?.playlistCount || 0,
    };

    const handleClose = () => {
        handleCloseCB();
    };

    // on submit form
    const handleOnSubmit = async (values: FormikValues) => {
        const existingHeroListing = railList.filter(
            (rail: any) => rail.template == HERO_CAROUSEL
        );
        const existingCWListing = railList.filter(
            (rail: any) => rail.template == CW_CAROUSEL
        );
        const existingMyList = railList.filter(
            (rail: any) => rail.template == MY_LIST
        );

        if (
            selectedRail?.payload?.template !== HERO_CAROUSEL &&
            values.template == HERO_CAROUSEL &&
            existingHeroListing.length
        ) {
            enqueueSnackbar(
                "Hero Carousel is already present, Cannot add more than one",
                { variant: "warning" }
            );
        } else if (
            selectedRail?.payload?.template !== CW_CAROUSEL &&
            values.template == CW_CAROUSEL &&
            existingCWListing.length
        ) {
            enqueueSnackbar(
                "Continue Watching is already present, Cannot add more than one",
                { variant: "warning" }
            );
        } else if (
            selectedRail?.payload?.template !== MY_LIST &&
            values.template == MY_LIST &&
            existingMyList.length
        ) {
            enqueueSnackbar(
                "My List is already present, Cannot add more than one",
                { variant: "warning" }
            );
        } else {
            // if the popup is for edit
            if (selectedRail?.payload) {
                handleEditRail(
                    { ...values, unlock: selectedRail?.payload?.unlock },
                    selectedRail?.index,
                    selectedRail?.type
                );
            }
            // for new rail
            else {
                handleAddRail(values);
            }
        }
    };

    // call API for getting the master information
    const getMasterInfo = async () => {
        try {
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/page/master`
            );
            if (materInformation.data?.success) {
                const rails = hideMyListRail(
                    materInformation.data?.data?.railTypes
                );
                setMasterData(rails);
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    const hideMyListRail = (railTypes: any[]) => {
        try {
            if (template == HOME_PAGE)
                return railTypes.filter((rail: any) =>
                    rail?.template.includes(HOME_PAGE)
                );
            else if (template == SERIES_PAGE)
                return railTypes.filter((rail: any) =>
                    rail?.template.includes(SERIES_PAGE)
                );
            else if (template == MOVIES_PAGE)
                return railTypes.filter((rail: any) =>
                    rail?.template.includes(MOVIES_PAGE)
                );
            else if (template == POPULAR_SEARCH) {
                const existingRailTemplates = railList.map(
                    (rail: any) => rail.template
                );
                let validRailTypes = [];
                if (selectedRail?.payload) {
                    validRailTypes = railTypes.filter((rail: any) =>
                        rail.template.includes(POPULAR_SEARCH)
                    );
                } else {
                    validRailTypes = railTypes.filter(
                        (rail: any) =>
                            rail.template.includes(POPULAR_SEARCH) &&
                            !existingRailTemplates.includes(rail.key)
                    );
                }
                return validRailTypes;
            } else
                return railTypes.filter(
                    (rail: any) =>
                        rail?.template == HOME_PAGE && rail.key !== MY_LIST
                );
        } catch (error) {
            return [];
        }
    };

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: any) => option.name + option.id,
    });

    const renderOptionsInitial = (options: any) => {
        if (typeof options == "string") {
            const filterPlaylist = playlist.filter(
                (list: any) => list.id === options
            );
            if (filterPlaylist.length > 0) {
                return filterPlaylist[0].name + " - " + filterPlaylist[0].id;
            } else {
                return "";
            }
        } else if (typeof options == "object") {
            return options.name + " - " + options.id;
        }
    };

    return (
        <div>
            {/* This is a popup modal for handling save config */}
            <BootstrapDialog
                fullWidth
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <BootstrapDialogTitle
                    id="customized-dialog-title"
                    onClose={handleClose}
                >
                    {isEditRail ? "Edit Rail" : "Add New Rail"}
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    {/* Form contents for handling save configuration starts here */}
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleOnSubmit}
                    >
                        {({
                            values,
                            handleChange,
                            touched,
                            errors,
                            setFieldValue,
                            setFieldError,
                            setFieldTouched,
                            setValues,
                        }) => (
                            <Form className="config-form">
                                {!(values.template == COLLECTIONS) && (
                                    <Field
                                        name="railTitle"
                                        as={TextField}
                                        label="Rail title"
                                        value={values.railTitle}
                                        onChange={handleChange}
                                        error={
                                            touched.railTitle &&
                                            errors.railTitle
                                        }
                                        helperText={
                                            touched.railTitle &&
                                            errors.railTitle
                                        }
                                    />
                                )}

                                <Field
                                    name="template"
                                    as={TextField}
                                    select
                                    label="Template"
                                    value={values.template}
                                    onChange={(e: any) => {
                                        handleChange(e);
                                        if (
                                            e.target.value == CW_CAROUSEL ||
                                            e.target.value == GENRE ||
                                            e.target.value == HERO_CAROUSEL ||
                                            e.target.value == THEMATIC ||
                                            e.target.value == SINGULAR_PROMO ||
                                            e.target.value == EPISODIC_RAIL ||
                                            e.target.value == MY_LIST
                                        ) {
                                            setValidationSchema(
                                                validationSchemaInitWithoutPlaylist
                                            );
                                            setFieldValue("playlistId", "");
                                        } else if (
                                            e.target.value == COLLECTIONS
                                        ) {
                                            setValidationSchema(
                                                validationSchemaTemplateOnly
                                            );
                                            setFieldValue("playlistId", "");
                                            setFieldValue("railTitle", "");
                                        } else {
                                            setValidationSchema(
                                                validationSchemaInitWithPlaylist
                                            );
                                        }
                                    }}
                                    disabled={isEditRail}
                                    error={touched.template && errors.template}
                                    helperText={
                                        touched.template && errors.template
                                    }
                                >
                                    {masterData.map(
                                        (master: any, index: number) => {
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    value={master.key}
                                                >
                                                    {master.label}
                                                </MenuItem>
                                            );
                                        }
                                    )}
                                </Field>
                                {!(
                                    values.template == CW_CAROUSEL ||
                                    values.template == GENRE ||
                                    values.template == HERO_CAROUSEL ||
                                    values.template == THEMATIC ||
                                    values.template == SINGULAR_PROMO ||
                                    values.template == EPISODIC_RAIL ||
                                    values.template == MY_LIST ||
                                    values.template == COLLECTIONS
                                ) && (
                                    <Autocomplete
                                        id="bcPlaylistId"
                                        options={playlistInfo}
                                        filterOptions={filterOptions}
                                        value={values.playlistId}
                                        includeInputInList
                                        open={isOpen}
                                        onChange={async (e, value) => {
                                            setFieldTouched(`playlistId`, true);
                                            const playlistCount =
                                                await getPlaylistCount(
                                                    value?.id
                                                );
                                            const formValue = {
                                                ...values,
                                                playlistId: value?.id || "",
                                                playlistCount,
                                            };
                                            setValues({ ...formValue });
                                        }}
                                        onOpen={handleOpenOptions}
                                        onClose={handleCloseOptions}
                                        getOptionLabel={(options: any) => {
                                            return (
                                                renderOptionsInitial(options) ||
                                                ""
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                name="playlistId"
                                                label="BC Playlist Id"
                                                value={values.playlistId}
                                                error={
                                                    touched.playlistId &&
                                                    (Boolean(
                                                        errors.playlistId
                                                    ) ||
                                                        Boolean(
                                                            errors.playlistCount
                                                        ))
                                                }
                                                helperText={
                                                    touched.playlistId &&
                                                    values[`playlistId`] &&
                                                    !values[`playlistCount`] &&
                                                    NO_CONTENT_PLAYLIST
                                                }
                                                {...params}
                                            />
                                        )}
                                    />
                                )}

                                <Button
                                    className="max-content-width float-center"
                                    variant="outlined"
                                    type="submit"
                                    disabled={submitting}
                                >
                                    {isEditRail ? "Update Rail" : "Add Rail"}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                    {/* Form contents for handling save configuration ends here */}
                </DialogContent>
            </BootstrapDialog>
        </div>
    );
}
